import { useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DocumentFilePicker } from "components/Document/shared";
import { useForceCompleteSubmission } from "hooks/useSubmissionMutations";
import { useUpsertDocument } from "hooks/useUpsertDocument";
import { DocumentType } from "generated-graphql/graphql";
import { uploadFileDirectly } from "util/uploadFile";

interface ForceCompleteDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
  tenantId: string;
  onSuccess?: () => void;
}

export function ForceCompleteDialog({
  open,
  onClose,
  submissionId,
  tenantId,
  onSuccess,
}: ForceCompleteDialogProps) {
  const alerts = useAlerts();
  const [submissionFiles, setSubmissionFiles] = useState<File[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [reason, setReason] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [forceComplete, { loading: forceCompleteLoading }] =
    useForceCompleteSubmission();
  const { upsertDocument } = useUpsertDocument();

  const loading = isUploading || forceCompleteLoading;

  const handleSubmissionFileDrop = useCallback((droppedFiles: File[]) => {
    // Only allow one submission document - take the first file
    if (droppedFiles.length > 0) {
      setSubmissionFiles([droppedFiles[0]]);
    }
  }, []);

  const handleSubmissionFileRemove = useCallback((index: number) => {
    setSubmissionFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAttachmentFileDrop = useCallback((droppedFiles: File[]) => {
    setAttachmentFiles((prev) => [...prev, ...droppedFiles]);
  }, []);

  const handleAttachmentFileRemove = useCallback((index: number) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetAndClose = useCallback(() => {
    setSubmissionFiles([]);
    setAttachmentFiles([]);
    setReason("");
    onClose();
  }, [onClose]);

  const handleClose = () => {
    if (loading) return;
    resetAndClose();
  };

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    const documentIds: string[] = [];

    for (const file of files) {
      const fileExtension = file.name.split(".").pop() ?? "";

      // Upload file to S3
      const storageLink = await uploadFileDirectly(file, { tenantId });

      // Create document record
      const documentId = await upsertDocument({
        documentData: {
          tenantId,
          title: file.name,
          documentType: DocumentType.Submission,
          fileExtension,
          storageLink,
        },
        currentSubmissionDocument: false,
      });

      if (documentId) {
        documentIds.push(documentId);
      }
    }

    return documentIds;
  };

  const handleSubmit = async () => {
    if (submissionFiles.length === 0) {
      alerts.error("Please upload at least one submission document");
      return;
    }

    if (!reason.trim()) {
      alerts.error("Please provide a reason for bypassing the workflow");
      return;
    }

    setIsUploading(true);

    try {
      // Upload submission documents
      const submissionDocumentIds = await uploadFiles(submissionFiles);

      if (submissionDocumentIds.length === 0) {
        throw new Error("Failed to upload submission documents");
      }

      // Upload attachment documents (if any)
      const attachmentDocumentIds =
        attachmentFiles.length > 0 ? await uploadFiles(attachmentFiles) : [];

      // Call force complete mutation
      await forceComplete({
        variables: {
          input: {
            submissionId,
            submissionDocumentIds,
            attachmentDocumentIds:
              attachmentDocumentIds.length > 0
                ? attachmentDocumentIds
                : undefined,
            reason: reason.trim(),
          },
        },
      });

      alerts.success("Submission marked as filing complete");
      onSuccess?.();
      resetAndClose();
    } catch (error) {
      alerts.error("Failed to force complete submission", error as Error);
    } finally {
      setIsUploading(false);
    }
  };

  const canSubmit =
    submissionFiles.length > 0 && reason.trim().length > 0 && !loading;

  return (
    <SafeDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Force Complete Submission</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="warning">
            This will bypass the normal review/signature workflow and mark the
            submission as filing complete. Use this when reviews or signatures
            were completed outside of Encamp.
          </Alert>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Submission Document *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Upload the final or signed submission document.
            </Typography>
            <DocumentFilePicker
              files={submissionFiles}
              onDrop={handleSubmissionFileDrop}
              onRemove={handleSubmissionFileRemove}
              canAddMore={submissionFiles.length === 0}
              multiple={false}
              disabled={loading}
              showPreviewButton={false}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Additional Attachment(s)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Optional supporting documents.
            </Typography>
            <DocumentFilePicker
              files={attachmentFiles}
              onDrop={handleAttachmentFileDrop}
              onRemove={handleAttachmentFileRemove}
              canAddMore={true}
              multiple={true}
              disabled={loading}
              showPreviewButton={false}
            />
          </Box>

          <TextField
            label="Reason for bypassing workflow *"
            placeholder="e.g., Customer approved via email on 2024-01-15"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={loading}
            helperText="This will be recorded as a system comment for audit purposes."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? "Processing..." : "Complete Submission"}
        </Button>
      </DialogActions>
    </SafeDialog>
  );
}
